// saju.narrative — a Saju book page's text boxes of one scope, drawn line by line as the book prints them (specs/011 §3,
// §12). The boxes keep their pieces: a manuscript slot (the text written for the person, props.text.slots), a figure the
// chart computes (template-saju-figures.mjs, snapshot.data.figures), and the words that stay (frame words, catalogue
// lines, layout numbers). A manuscript was written from facts (props.text.factsHash); when the person changes, the facts
// are calculated again and a text written from others is to be written again. A page's boxes holding only figures are
// its `book` component: nothing in them is ever to be rewritten.
import { SUBJECT_KEYS, componentError as error, requestSaju, sameSubject, validateCalculation, validateSubject } from './component-saju.mjs';
import { factsHash, readBookChart, scopeFacts } from './template-saju-facts.mjs';
import { FIGURE_FRAME_KEYS, bookFigures } from './template-saju-figures.mjs';
import { SAJU_BOOK_FRAME } from './template-saju-book.mjs';
import { asPrinted, loadPrintMetrics, printMetrics } from './print-wrap.mjs';

const SOURCE = 'studio:saju-narrative';
const PAGE = Object.freeze({ width: 760, height: 1078 });
// The book's three faces as the canvas names them (canvas_scenes.py SceneRecorder.text).
const FONTS = Object.freeze({ body: Object.freeze(['Content KoPub Dotum', '400']), bold: Object.freeze(['Content KoPub Dotum', '700']),
  serif: Object.freeze(['Content KoPub Batang', '700']) });
const REVIEW_STATES = Object.freeze(['sample', 'draft', 'reviewed']);
const FIGURE_KEY = /^[a-z0-9-]+(?:\/[a-z0-9-]+){0,3}$/;
// What a figure prints when this person's chart has no such value (a year outside their hundred): never another's text.
export const MISSING_FIGURE = '[계산 불가]';

const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const finite = value => typeof value === 'number' && Number.isFinite(value);
const exactKeys = (value, required, optional = []) => object(value) && required.every(key => Object.hasOwn(value, key))
  && Object.keys(value).every(key => required.includes(key) || optional.includes(key));
const invalid = message => error('INVALID_COMPONENT', `원고 컴포넌트: ${message}`);

// The frame words that are a product's own reading of the sample's chart — the wealth and personality books' pages
// (specs/011 §11): kept in the builder's frame copy, yet the product's manuscript, written from the natal facts.
export const PRODUCT_READING = /^frame\/native-(?:wealth|personality)\./;

/** The manuscript addresses a scope's text is written in: series/daeun/6/…, series/seun/2026/…, the product's doc/… and readings. */
function ownsAddress(scope, address) {
  if (scope.series === 'daeun') return address.startsWith(`series/daeun/${scope.period}/`);
  if (scope.series === 'seun') return address.startsWith(`series/seun/${scope.year}/`);
  return scope.series === 'product' && (address.startsWith('doc/') || PRODUCT_READING.test(address));
}
function validateScope(scope) {
  if (!object(scope)) throw invalid('범위가 없습니다.');
  const product = typeof scope.product === 'string' && /^[a-z0-9-]{1,80}$/.test(scope.product);
  const valid = scope.series === 'daeun' ? exactKeys(scope, ['series', 'period']) && Number.isInteger(scope.period) && scope.period >= 1 && scope.period <= 10
    : scope.series === 'seun' ? exactKeys(scope, ['series', 'year']) && Number.isInteger(scope.year) && scope.year >= 1900 && scope.year <= 2300
      : ['product', 'book'].includes(scope.series) && exactKeys(scope, ['series', 'product']) && product;
  if (!valid) throw invalid('범위는 대운 한 편, 세운 한 해, 상품 글 또는 책의 계산값입니다.');
}
function validateSegment(segment, scope) {
  if (exactKeys(segment, ['text'])) {
    if (typeof segment.text !== 'string' || !ownsAddress(scope, segment.text)) throw invalid(`원고 조각 ${segment.text} 는 이 범위의 칸이 아닙니다.`);
  } else if (exactKeys(segment, ['figure'], ['literal'])) {
    // `literal`: what the book printed there — the Studio tells when a figure has moved from it (book-text.mjs).
    if (typeof segment.figure !== 'string' || !FIGURE_KEY.test(segment.figure)
      || (Object.hasOwn(segment, 'literal') && (typeof segment.literal !== 'string' || segment.literal.length > 20000))) throw invalid('계산값 조각의 키가 올바르지 않습니다.');
  } else if (exactKeys(segment, ['literal'], ['address', 'layout'])) {
    if (typeof segment.literal !== 'string' || segment.literal.length > 20000 || (Object.hasOwn(segment, 'address') && Object.hasOwn(segment, 'layout'))
      || ['address', 'layout'].some(key => Object.hasOwn(segment, key) && (typeof segment[key] !== 'string' || !segment[key] || segment[key].length > 200))) throw invalid('글자 조각이 올바르지 않습니다.');
  } else throw invalid('조각은 원고 칸, 계산값 또는 글자입니다.');
}
function validateBox(box, scope) {
  if (!exactKeys(box, ['id', 'x', 'y', 'width', 'size', 'font', 'color', 'height', 'lines', 'center', 'leading', 'segments'])) throw invalid('글상자 필드를 확인해 주세요.');
  if (typeof box.id !== 'string' || !box.id || box.id.length > 200) throw invalid('글상자 ID가 올바르지 않습니다.');
  if (![box.x, box.y, box.width, box.size, box.leading].every(finite) || box.width <= 0 || box.size < 8 || box.size > 200 || box.leading <= 0) throw invalid('글상자 위치·크기가 올바르지 않습니다.');
  if (!Object.hasOwn(FONTS, box.font) || typeof box.color !== 'string' || !/^#[0-9a-f]{6}$/i.test(box.color)) throw invalid('글상자 글꼴·색이 올바르지 않습니다.');
  if ((box.height !== null && (!finite(box.height) || box.height <= 0)) || !Number.isInteger(box.lines) || box.lines < 1 || box.lines > 400 || typeof box.center !== 'boolean') throw invalid('글상자 줄 예산이 올바르지 않습니다.');
  if (!Array.isArray(box.segments) || !box.segments.length || box.segments.length > 400) throw invalid('글상자 조각이 올바르지 않습니다.');
  box.segments.forEach(segment => validateSegment(segment, scope));
}
function validateText(text, props) {
  if (!exactKeys(text, ['slots', 'factsHash', 'review'], ['basis', 'citations', 'generator']) || !object(text.slots)) throw invalid('원고 글 기록을 확인해 주세요.');
  const used = new Set(props.boxes.flatMap(box => box.segments.filter(segment => segment.text).map(segment => segment.text)));
  const slots = Object.keys(text.slots);
  if (slots.length !== used.size || slots.some(address => !used.has(address)) || Object.values(text.slots).some(value => typeof value !== 'string' || value.length > 20000)) throw invalid('원고 칸의 글이 상자의 원고 조각과 맞지 않습니다.');
  const book = props.scope.series === 'book';
  if (book ? text.factsHash !== null || slots.length : typeof text.factsHash !== 'string' || !/^[0-9a-f]{64}$/.test(text.factsHash)) throw invalid('원고가 쓰인 사실의 해시가 올바르지 않습니다.');
  if (!exactKeys(text.review, ['state'], ['by', 'at']) || !REVIEW_STATES.includes(text.review.state)
    || ['by', 'at'].some(key => Object.hasOwn(text.review, key) && (typeof text.review[key] !== 'string' || !text.review[key].trim() || text.review[key].length > 120))) throw invalid('원고 검토 상태가 올바르지 않습니다.');
  // What a written text rests on: fact paths (narrative-writing.mjs FACT_PATH), the wiki pages it follows, who wrote it.
  if (Object.hasOwn(text, 'basis') && (!Array.isArray(text.basis) || !text.basis.length || text.basis.length > 200
    || text.basis.some(path => typeof path !== 'string' || path.length > 200 || !/^[\p{L}_][\p{L}\p{N}_]*(?:\.[\p{L}_][\p{L}\p{N}_]*|\[\d{1,3}\])*$/u.test(path)))) throw invalid('원고의 근거 사실 경로가 올바르지 않습니다.');
  if (Object.hasOwn(text, 'citations') && (!Array.isArray(text.citations) || !text.citations.length || text.citations.length > 50
    || text.citations.some(item => !exactKeys(item, ['wiki']) || typeof item.wiki !== 'string' || item.wiki.length > 300 || !/^[^/.#\s][^#\s]*\.md(?:#[^#]+)?$/.test(item.wiki) || item.wiki.includes('..')))) throw invalid('원고의 wiki 인용이 올바르지 않습니다.');
  if (Object.hasOwn(text, 'generator') && (!exactKeys(text.generator, ['agent', 'guide'])
    || ['agent', 'guide'].some(key => typeof text.generator[key] !== 'string' || !text.generator[key].trim() || text.generator[key].length > 200))) throw invalid('원고를 쓴 도우미 기록이 올바르지 않습니다.');
}

const placeholders = text => [...new Set(text.match(/\{[a-z_][a-z0-9_]*\}/g) ?? [])].sort().join();
/** The edited frame words a book's figures were made with ({ key: text }): each one fills the same values as the Studio's. */
function validateFrame(frame) {
  if (!object(frame) || !Object.keys(frame).length) throw invalid('틀 문구 편집이 비어 있습니다.');
  for (const [key, text] of Object.entries(frame)) {
    if (!FIGURE_FRAME_KEYS.includes(key) || typeof text !== 'string' || !text.trim() || text.length > 2000
      || placeholders(text) !== placeholders(SAJU_BOOK_FRAME[key].text)) throw invalid(`계산값 틀 문구 ${key} 의 편집이 올바르지 않습니다.`);
  }
}
/** The frame words this component's figures are made with: the Studio's, with the book's edits over them. */
export function narrativeFrame(props) {
  if (!props.frame) return SAJU_BOOK_FRAME;
  return { ...SAJU_BOOK_FRAME, ...Object.fromEntries(Object.entries(props.frame).map(([key, text]) => [key, { ...SAJU_BOOK_FRAME[key], text }])) };
}

export function validateNarrativeProps(props) {
  if (!object(props) || Object.keys(props).some(key => ![...SUBJECT_KEYS, 'scope', 'boxes', 'text', 'person', 'frame'].includes(key))) throw invalid('설정 필드를 확인해 주세요.');
  validateSubject(props);
  validateScope(props.scope);
  if (!Array.isArray(props.boxes) || !props.boxes.length || props.boxes.length > 80) throw invalid('글상자는 1~80개입니다.');
  props.boxes.forEach(box => validateBox(box, props.scope));
  if (new Set(props.boxes.map(box => box.id)).size !== props.boxes.length) throw invalid('글상자 ID가 겹칩니다.');
  if (Object.hasOwn(props, 'person') && (!exactKeys(props.person, [], ['name', 'dataId'])
    || Object.values(props.person).some(value => typeof value !== 'string' || !value.trim() || value.length > 100))) throw invalid('이름·자료 식별값이 올바르지 않습니다.');
  if (Object.hasOwn(props, 'frame')) validateFrame(props.frame);
  validateText(props.text, props);
  return props;
}

function validateNarrativeData(data) {
  if (!exactKeys(data, ['factsHash', 'figures']) || !object(data.figures)) throw invalid('계산 결과 필드를 확인해 주세요.');
  if (Object.entries(data.figures).some(([key, value]) => !FIGURE_KEY.test(key) || typeof value !== 'string' || value.length > 20000)) throw invalid('계산값이 올바르지 않습니다.');
  if (data.factsHash !== null && (typeof data.factsHash !== 'string' || !/^[0-9a-f]{64}$/.test(data.factsHash))) throw invalid('사실의 해시가 올바르지 않습니다.');
}

/** The figure keys a component's boxes print. */
export const figureKeys = props => [...new Set(props.boxes.flatMap(box => box.segments.filter(segment => segment.figure).map(segment => segment.figure)))];

/**
 * The calculation of a component from its person's book chart (readBookChart): its figures, and the hash of the facts its
 * text rests on. The facts themselves are not kept — a page of a book holds several components and a book hundreds, and
 * whoever writes the text asks for them (scopeFacts) from the same person. `figures` is the book's bookFigures when the
 * caller has made them already (an import makes them once for every page).
 */
export async function narrativeSnapshot(props, book, figures = bookFigures(book, { frame: narrativeFrame(props), year: props.year, ...(props.person ?? {}) })) {
  const data = { figures: Object.fromEntries(figureKeys(props).filter(key => Object.hasOwn(figures, key)).map(key => [key, figures[key]])),
    factsHash: props.scope.series === 'book' ? null : await factsHash(scopeFacts(book, props.scope)) };
  return { source: SOURCE, calculatedAt: new Date().toISOString(), data };
}

// A book has a component per scope on every page (hundreds): they share their person's chart, asked of the engine once,
// and its figures, made once per name, data id and edited frame words. The last few people are kept (a book's person and
// the one tried).
const books = new Map();
function remembered(key, make) {
  if (!books.has(key)) {
    books.set(key, make().catch(reason => { books.delete(key); throw reason; }));
    while (books.size > 4) books.delete(books.keys().next().value);
  }
  return books.get(key);
}
function bookOf(props, options) {
  const person = JSON.stringify(SUBJECT_KEYS.map(key => props[key] ?? null));
  return remembered(person, async () => {
    const book = readBookChart(await requestSaju('chart', props, options));
    return { book, figures: new Map() };
  });
}

export async function calculateNarrative(props, options = {}) {
  validateNarrativeProps(props);
  const [{ book, figures }] = await Promise.all([bookOf(props, options), loadPrintMetrics()]);
  const named = JSON.stringify([props.person ?? {}, props.frame ?? {}]);
  if (!figures.has(named)) figures.set(named, bookFigures(book, { frame: narrativeFrame(props), year: props.year, ...(props.person ?? {}) }));
  return narrativeSnapshot(props, book, figures.get(named));
}

/** The facts a component's text rests on for its person now (scopeFacts): what a writer writes from. */
export async function narrativeFacts(props, options = {}) {
  const { book } = await bookOf(props, options);
  return scopeFacts(book, props.scope);
}

/** A box's text for this calculation: its manuscript slots, its figures (or MISSING_FIGURE), its words, as Book.text prints them. */
export function boxText(props, snapshot, box) {
  return asPrinted(box.segments.map(segment => segment.text ? props.text.slots[segment.text]
    : segment.figure ? snapshot.data.figures[segment.figure] ?? MISSING_FIGURE : segment.literal).join(''));
}

export function renderNarrative(props, snapshot) {
  validateNarrativeProps(props);
  const metrics = printMetrics();
  if (!metrics) throw error('PRINT_METRICS_NOT_LOADED', '인쇄 글자 폭 표를 아직 불러오지 않았습니다. 다시 계산해 주세요.');
  const elements = [];
  props.boxes.forEach((box, index) => {
    const [fontFamily, fontWeight] = FONTS[box.font];
    metrics.printedLines(box, boxText(props, snapshot, box)).forEach((line, n) => elements.push({ key: `box-${index}:line-${n}`, type: 'text', ...line,
      fontSize: box.size, fontFamily, fontWeight, fontStyle: 'normal', textAlign: 'left', color: box.color, autoWidth: true }));
  });
  return { width: PAGE.width, height: PAGE.height, elements };
}

/**
 * What stops the component's text from being printed as it is, for the Studio to show and the outputs to refuse (P5):
 * a manuscript written from other facts (`stale`), a figure this chart does not have, a box whose text no longer fits
 * its lines, a character the book's fonts cannot print.
 */
export function narrativeIssues(props, snapshot) {
  const issues = [];
  if (props.scope.series !== 'book' && props.text.factsHash !== snapshot.data.factsHash) issues.push({ kind: 'stale', scope: props.scope });
  for (const key of figureKeys(props)) if (!Object.hasOwn(snapshot.data.figures, key)) issues.push({ kind: 'missing-figure', key });
  const metrics = printMetrics();
  if (metrics) for (const box of props.boxes) {
    const text = boxText(props, snapshot, box), lines = metrics.wrap(text, box.size, box.width, box.font).length;
    if (!metrics.fits(lines, box.size, box.height ?? box.lines * box.leading)) issues.push({ kind: 'overflow', box: box.id, lines });
    const unsupported = metrics.unsupported(text, box.font);
    if (unsupported.length) issues.push({ kind: 'unsupported', box: box.id, characters: unsupported });
  }
  return issues;
}

/**
 * Where one person's text for a scope's slot differs between components (specs/011 P4-3): a text is written once for the
 * person and prints wherever the book reads it, so its components agree — the later one of a pair is named.
 */
export function narrativeTextConflicts(components) {
  const written = new Map(), conflicts = [];
  components.forEach((component, index) => {
    const { props } = component, whose = JSON.stringify([SUBJECT_KEYS.map(key => props[key]), props.scope]);
    for (const [address, text] of Object.entries(props.text.slots)) {
      const key = `${whose}|${address}`;
      if (!written.has(key)) written.set(key, text);
      else if (written.get(key) !== text) conflicts.push({ index, message: `원고 칸 ${address} 가 같은 명식의 다른 쪽과 다릅니다. 한 사람의 원고는 모든 쪽에서 같아야 합니다.` });
    }
  });
  return conflicts;
}

/**
 * The figures that print otherwise than the book printed them ([key]): the page's pictures beside them — an arrow for a
 * score change, a month's icon — were drawn for the printed value (book-text.mjs keeps such a page from the outputs).
 */
export function movedFigures(props, snapshot) {
  return [...new Set(props.boxes.flatMap(box => box.segments.filter(segment => segment.figure && Object.hasOwn(segment, 'literal')
    && asPrinted(snapshot.data.figures[segment.figure] ?? MISSING_FIGURE) !== segment.literal).map(segment => segment.figure)))];
}

/** The boxes (their order in props.boxes) whose text narrativeIssues names: all that hold a manuscript when it is stale. */
export function narrativeIssueBoxes(props, snapshot) {
  const boxes = new Set();
  for (const issue of narrativeIssues(props, snapshot)) props.boxes.forEach((box, index) => {
    if (issue.kind === 'stale' ? box.segments.some(segment => segment.text) : issue.kind === 'missing-figure'
      ? box.segments.some(segment => segment.figure === issue.key) : box.id === issue.box) boxes.add(index);
  });
  return [...boxes].sort((a, b) => a - b);
}

const REVIEW_LABELS = Object.freeze({ sample: '표본 원고', draft: '검토 전 초안', reviewed: '검토 완료' });
const ISSUE_LABELS = Object.freeze({ stale: '지금 명식의 사실과 달라 다시 써야 합니다', 'missing-figure': '이 명식에 없는 계산값',
  overflow: '줄을 넘친 글상자', unsupported: '인쇄 글꼴에 없는 글자' });
/** A component's text as the panel tells it: its review state and what keeps it from printing (narrativeIssues). */
export function narrativeState(props, snapshot) {
  const issues = narrativeIssues(props, snapshot), counts = {};
  for (const issue of issues) counts[issue.kind] = (counts[issue.kind] ?? 0) + 1;
  const told = Object.entries(counts).map(([kind, count]) => kind === 'stale' ? ISSUE_LABELS.stale : `${ISSUE_LABELS[kind]} ${count}곳`);
  const scope = props.scope.series === 'book' ? '계산값만' : REVIEW_LABELS[props.text.review.state];
  return `${describeNarrative(props)} · ${scope}${told.length ? ` — ${told.join(' · ')}` : ''}${printMetrics() ? '' : ' (줄바꿈은 아직 확인하지 않았습니다)'}`;
}

/** How the page's component list names one: the scope its text is written for. */
export function describeNarrative(props) {
  const { scope } = props;
  return scope.series === 'daeun' ? `${scope.period}대운` : scope.series === 'seun' ? `${scope.year}년` : scope.series === 'product' ? '상품 글' : '책의 계산값';
}

// perComponent: each component is calculated for its own scope and boxes (one chart request serves them all).
export const NARRATIVE_COMPONENT = Object.freeze({ type: 'saju.narrative', version: 1, label: '사주 원고', insertable: false, perComponent: true, describe: describeNarrative,
  description: '사주 책 쪽의 글상자: 원고 칸·계산값·틀 문구를 인쇄와 같은 줄로 그립니다. 책을 가져올 때 만들어집니다.',
  fields: Object.freeze([]),
  validateProps: validateNarrativeProps,
  validateSnapshot: snapshot => validateCalculation(snapshot, SOURCE, validateNarrativeData),
  calculate: calculateNarrative, render: renderNarrative, validateDocument: narrativeTextConflicts,
  calculationChanged: (before, after) => !sameSubject(before, after) || JSON.stringify([before.person ?? {}, before.frame ?? {}]) !== JSON.stringify([after.person ?? {}, after.frame ?? {}]) });
